// src/pages/rules/RulesConfigPage.jsx
//
// SOW Rules Engine admin page. State/handlers live in useRulesConfig();
// this file is composition only -- see RulesTable.jsx, RuleFormModal.jsx,
// and DeleteRuleModal.jsx for the three pieces of UI it assembles.
import { Plus } from "lucide-react";
import { Alert, Button, PageHeader } from "../../shared/ui";
import { useRulesConfig } from "./useRulesConfig";
import RulesTable from "./RulesTable";
import RuleFormModal from "./RuleFormModal";
import DeleteRuleModal from "./DeleteRuleModal";

export default function RulesConfigPage() {
  const rc = useRulesConfig();

  // All hooks inside useRulesConfig() still run every render regardless of
  // permission -- this check only decides what to render, never how many
  // hooks fire.
  if (!rc.canViewPage) {
    return (
      <div className="p-6">
        <Alert variant="danger" title="Access denied">
          You don't have access to the SOW rule catalog. Ask an account or org admin to grant you
          sow:rules:view.
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="SOW Compliance Rules"
        subtitle="Configure the scoring rules SOW Analysis evaluates documents against, per contract type — with factor-based weighting (Impact × Likelihood × Recoverability × Applicability)."
        actions={
          <Button onClick={rc.openAddForm}>
            <Plus size={15} /> Add Rule
          </Button>
        }
      />

      {rc.message && (
        <Alert variant="success" className="mb-4">
          {rc.message}
        </Alert>
      )}
      {rc.error && (
        <Alert variant="danger" className="mb-4">
          {rc.error}
        </Alert>
      )}

      <RulesTable
        rules={rc.rules}
        loading={rc.loading}
        hasActiveFilters={rc.hasActiveFilters}
        canManageRule={rc.canManageRule}
        canManageGlobal={rc.canManageGlobal}
        canManageOrgWide={rc.canManageOrgWide}
        manageableAccountIds={rc.manageableAccountIds}
        accountNameById={rc.accountNameById}
        onEdit={rc.onEdit}
        onDeleteRequest={rc.setDeleteTarget}
        openAddForm={rc.openAddForm}
        searchInput={rc.searchInput}
        onSearchChange={(e) => rc.setSearchInput(e.target.value)}
        scopeFilter={rc.scopeFilter}
        onScopeFilterChange={(e) => {
          rc.setScopeFilter(e.target.value);
          rc.setPage(1);
        }}
        filterType={rc.filterType}
        onFilterTypeChange={(e) => {
          rc.setFilterType(e.target.value);
          rc.setPage(1);
        }}
        filterOptions={rc.filterOptions}
        page={rc.page}
        totalPages={rc.totalPages}
        onPageChange={rc.setPage}
      />

      <RuleFormModal
        open={rc.formOpen}
        editingId={rc.editingId}
        canCreateAnything={rc.canCreateAnything}
        onClose={rc.onCancel}
        onSubmit={rc.onSubmit}
        saving={rc.saving}
        form={rc.form}
        onChange={rc.onChange}
        scope={rc.scope}
        onScopeChange={(e) => {
          rc.setScope(e.target.value);
          rc.setScopeAccountId("");
        }}
        scopeSelectOptions={rc.scopeSelectOptions}
        scopeAccountId={rc.scopeAccountId}
        onScopeAccountChange={(e) => rc.setScopeAccountId(e.target.value)}
        selectableAccounts={rc.selectableAccounts}
        accountNameById={rc.accountNameById}
        typeMode={rc.typeMode}
        customType={rc.customType}
        contractTypeSelectValue={rc.contractTypeSelectValue}
        contractTypeOptions={rc.contractTypeOptions}
        onContractTypeSelectChange={rc.onContractTypeSelectChange}
        onCustomTypeChange={rc.onCustomTypeChange}
        categoryMode={rc.categoryMode}
        categoryCustom={rc.categoryCustom}
        categorySelectValue={rc.categorySelectValue}
        categoryOptions={rc.categoryOptions}
        onCategorySelectChange={rc.onCategorySelectChange}
        onCategoryCustomChange={rc.onCategoryCustomChange}
        dimensionMode={rc.dimensionMode}
        dimensionCustom={rc.dimensionCustom}
        dimensionSelectValue={rc.dimensionSelectValue}
        dimensionOptions={rc.dimensionOptions}
        onDimensionSelectChange={rc.onDimensionSelectChange}
        onDimensionCustomChange={rc.onDimensionCustomChange}
        computedWeight={rc.computedWeight}
        normalizedWeight={rc.normalizedWeight}
      />

      <DeleteRuleModal
        target={rc.deleteTarget}
        onClose={() => rc.setDeleteTarget(null)}
        onConfirm={rc.onDelete}
      />
    </div>
  );
}
